import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PROFILE_TYPE_LABEL } from "@/lib/constants";
import { locationLine } from "@/lib/format";
import ProfileCard from "@/components/shared/ProfileCard";
import GigCard from "@/components/shared/GigCard";
import EmptyState from "@/components/shared/EmptyState";
import { Music2 } from "lucide-react";

export default function Dashboard() {
  const { account, activeProfile } = useAuth();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ my_apps: 0, received: 0 });
  const isVenue = activeProfile?.profile_type === "venue";

  useEffect(() => {
    if (!activeProfile) return;
    (async () => {
      try {
        if (isVenue) {
          const { data } = await api.get("/talent");
          setFeatured(data.slice(0, 6));
        } else {
          const { data } = await api.get("/opportunities");
          setFeatured(data.slice(0, 6));
        }
      } catch {}
      const [{ data: mine }, { data: rec }] = await Promise.all([
        api.get("/applications", { params: { role: "applicant" } }).catch(() => ({ data: [] })),
        api.get("/applications", { params: { role: "poster" } }).catch(() => ({ data: [] })),
      ]);
      setStats({ my_apps: mine.length, received: rec.length });
    })();
  }, [activeProfile, isVenue]);

  if (!activeProfile) {
    return (
      <AppLayout>
        <EmptyState
          title="Create your first profile"
          description="Musician, Band, DJ or Venue — pick one to get started."
          action={<Button onClick={() => navigate("/profiles/add")} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">Add profile</Button>}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back, {account?.name?.split(" ")[0]}</h1>
        <p className="text-slate-600 mt-1">
          Using your <b>{PROFILE_TYPE_LABEL[activeProfile.profile_type]}</b> profile — {activeProfile.display_name} · {locationLine(activeProfile.city, activeProfile.state)}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
        <Link to="/applications?role=applicant" data-testid="stat-my-apps"
          className="border border-slate-200 rounded-xl p-4 bg-white hover:border-slate-900">
          <div className="text-xs uppercase text-slate-500 tracking-wider">My applications</div>
          <div className="mt-1 font-display text-2xl font-bold">{stats.my_apps}</div>
        </Link>
        <Link to="/applications?role=poster" data-testid="stat-received"
          className="border border-slate-200 rounded-xl p-4 bg-white hover:border-slate-900">
          <div className="text-xs uppercase text-slate-500 tracking-wider">Received</div>
          <div className="mt-1 font-display text-2xl font-bold">{stats.received}</div>
        </Link>
        <Link to="/profiles" className="border border-slate-200 rounded-xl p-4 bg-white hover:border-slate-900" data-testid="stat-profiles">
          <div className="text-xs uppercase text-slate-500 tracking-wider">Profiles</div>
          <div className="mt-1 font-display text-2xl font-bold">—</div>
        </Link>
      </div>

      <section className="mt-10">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-xl font-semibold tracking-tight">
            {isVenue ? "Recommended talent" : "Recommended gigs"}
          </h2>
          <Link to={isVenue ? "/explore?category=all" : "/explore?category=gigs"} className="text-sm text-[#4F46E5] hover:underline">View all →</Link>
        </div>
        {featured.length === 0 ? (
          <div className="mt-4"><EmptyState title="Nothing here yet" description="Post a gig or explore the marketplace." /></div>
        ) : isVenue ? (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((p) => <ProfileCard key={p.profile_id} profile={p} />)}
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {featured.map((op) => <GigCard key={op.opportunity_id} opportunity={op} />)}
          </div>
        )}
      </section>
    </AppLayout>
  );
}
